import { useEffect, useState } from 'react';
import { api, ApiError, type AuthStatus } from './lib/api';
import { Login } from './pages/Login';
import { Console } from './pages/Console';

type AuthState =
  | { status: 'checking' }
  | { status: 'logged_out' }
  | { status: 'logged_in'; auth: AuthStatus };

export function App() {
  const [auth, setAuth] = useState<AuthState>({ status: 'checking' });

  useEffect(() => {
    let cancelled = false;
    async function check() {
      try {
        const a = await api.authStatus();
        if (!cancelled) setAuth({ status: 'logged_in', auth: a });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && (err.status === 401 || err.code === 'no_session')) {
          setAuth({ status: 'logged_out' });
        } else {
          // On unexpected error, fall through to login screen with a message.
          setAuth({ status: 'logged_out' });
        }
      }
    }
    check();
    return () => {
      cancelled = true;
    };
  }, []);

  if (auth.status === 'checking') {
    return (
      <div className="flex h-[100dvh] items-center justify-center">
        <div className="text-paper-400 dark:text-paper-300 text-sm">Checking session.</div>
      </div>
    );
  }

  if (auth.status === 'logged_out') {
    return (
      <Login
        onSuccess={(a) => {
          setAuth({ status: 'logged_in', auth: a });
        }}
      />
    );
  }

  return (
    <Console
      auth={auth.auth}
      onLogout={async () => {
        try {
          await api.logout();
        } finally {
          setAuth({ status: 'logged_out' });
        }
      }}
      onSignOutEverywhere={async () => {
        const ok = window.confirm(
          'Sign out from every device, including this one? You will have to enter the secret to sign back in.',
        );
        if (!ok) return;
        try {
          await api.signOutEverywhere();
        } finally {
          setAuth({ status: 'logged_out' });
        }
      }}
    />
  );
}
